import React, { useEffect, useMemo, useRef, useState } from "react";

import { IoChevronBack, IoChevronForward } from "react-icons/io5";

import "./LineChart.scss";

const DAY_MS = 24 * 60 * 60 * 1000;

const parseDate = (value) => {
  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? null : value;
  }

  if (typeof value !== "string") {
    return null;
  }

  const [year, month, day] = value.split("-").map(Number);

  if (!year || !month || !day) {
    return null;
  }

  // Noon avoids timezone/DST edge cases for date-only history.
  const date = new Date(year, month - 1, day, 12);

  return Number.isNaN(date.getTime()) ? null : date;
};

const formatDateKey = (date) => {
  const year = date.getFullYear();

  const month = String(date.getMonth() + 1).padStart(2, "0");

  const day = String(date.getDate()).padStart(2, "0");

  return `${year}-${month}-${day}`;
};

const addDays = (date, days) => {
  const result = new Date(date);

  result.setDate(result.getDate() + days);

  return result;
};

const startOfWeek = (date) => {
  const result = new Date(date);

  const day = result.getDay();

  // Monday -> Sunday
  const diff = day === 0 ? -6 : 1 - day;

  result.setDate(result.getDate() + diff);

  result.setHours(12, 0, 0, 0);

  return result;
};

const calendarDayNumber = (date) =>
  Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()) / DAY_MS;

const weeksBetween = (start, end) =>
  Math.max(
    0,
    Math.floor((calendarDayNumber(end) - calendarDayNumber(start)) / 7),
  );

const formatRangeDate = (date, includeYear = false) =>
  date.toLocaleDateString("default", {
    month: "short",
    day: "numeric",
    ...(includeYear && {
      year: "numeric",
    }),
  });

const getWeekLabel = (start, end) => {
  const sameMonth =
    start.getMonth() === end.getMonth() &&
    start.getFullYear() === end.getFullYear();

  return {
    top: `${start.getDate()}–${end.getDate()}`,

    bottom: sameMonth
      ? start.toLocaleString("default", {
          month: "short",
        })
      : `${start.toLocaleString("default", {
          month: "short",
        })}/${end.toLocaleString("default", {
          month: "short",
        })}`,
  };
};

const getLineLength = (points) => {
  let length = 0;

  for (let i = 1; i < points.length; i++) {
    const dx = points[i].x - points[i - 1].x;

    const dy = points[i].y - points[i - 1].y;

    length += Math.sqrt(dx * dx + dy * dy);
  }

  return length;
};

const defaultFormatValue = (value) => {
  const number = Number(value) || 0;

  if (Math.abs(number) >= 1000) {
    return Intl.NumberFormat("default", {
      notation: "compact",
      maximumFractionDigits: 1,
    }).format(number);
  }

  if (Number.isInteger(number)) {
    return String(number);
  }

  return number.toFixed(2).replace(/0+$/, "").replace(/\.$/, "");
};

export default function LineChart({
  data = [],
  weeksPerPage = 6,

  valueAccessor = (item) => item?.checked ?? 0,

  unit = "",
  goalPerDay = null,

  maxY: maxYProp,
  yTickCount = 4,

  formatValue = defaultFormatValue,

  emptyLabel = "No activity yet",

  onPointPress,
}) {
  const [pageOffset, setPageOffset] = useState(0);

  const [tooltip, setTooltip] = useState(null);

  const tooltipTimer = useRef(null);

  const safeWeeksPerPage = Math.max(2, Math.round(weeksPerPage));

  const parsedData = useMemo(
    () =>
      data
        .map((item) => {
          const date = parseDate(item?.date);

          if (!date) {
            return null;
          }

          return {
            item,
            date,

            value: Number(valueAccessor(item)) || 0,
          };
        })
        .filter(Boolean),
    [data, valueAccessor],
  );

  const today = useMemo(() => new Date(), []);

  const earliestDate = useMemo(() => {
    if (!parsedData.length) {
      return today;
    }

    return parsedData.reduce(
      (earliest, entry) => (entry.date < earliest ? entry.date : earliest),
      parsedData[0].date,
    );
  }, [parsedData, today]);

  const latestDate = useMemo(() => {
    if (!parsedData.length) {
      return today;
    }

    const latestRecorded = parsedData.reduce(
      (latest, entry) => (entry.date > latest ? entry.date : latest),
      parsedData[0].date,
    );

    return latestRecorded > today ? latestRecorded : today;
  }, [parsedData, today]);

  const earliestWeek = useMemo(() => startOfWeek(earliestDate), [earliestDate]);

  const latestWeek = useMemo(() => startOfWeek(latestDate), [latestDate]);

  const totalWeeks = weeksBetween(earliestWeek, latestWeek) + 1;

  const totalPages = Math.max(1, Math.ceil(totalWeeks / safeWeeksPerPage));

  const maxPageOffset = totalPages - 1;

  useEffect(() => {
    setPageOffset((current) => Math.min(current, maxPageOffset));
  }, [maxPageOffset]);

  const weekTotals = useMemo(() => {
    const totals = new Map();

    parsedData.forEach(({ item, date, value }) => {
      const key = formatDateKey(startOfWeek(date));

      const current = totals.get(key) || {
        value: 0,
        items: [],
      };

      current.value += value;

      current.items.push(item);

      totals.set(key, current);
    });

    return totals;
  }, [parsedData]);

  // Use full history, not just the
  // current page, for a stable Y scale.
  const highestWeeklyValue = useMemo(() => {
    let highest = 0;

    weekTotals.forEach((week) => {
      highest = Math.max(highest, week.value);
    });

    return highest;
  }, [weekTotals]);

  const pageNewestWeek = useMemo(
    () => addDays(latestWeek, -pageOffset * safeWeeksPerPage * 7),
    [latestWeek, pageOffset, safeWeeksPerPage],
  );

  const pageStart = useMemo(
    () => addDays(pageNewestWeek, -(safeWeeksPerPage - 1) * 7),
    [pageNewestWeek, safeWeeksPerPage],
  );

  const pageEnd = useMemo(() => addDays(pageNewestWeek, 6), [pageNewestWeek]);

  const currentData = useMemo(
    () =>
      Array.from(
        {
          length: safeWeeksPerPage,
        },
        (_, index) => {
          const weekStart = addDays(pageStart, index * 7);

          const weekEnd = addDays(weekStart, 6);

          const stored = weekTotals.get(formatDateKey(weekStart));

          return {
            weekStart,
            weekEnd,

            value: stored?.value || 0,

            items: stored?.items || [],
          };
        },
      ),
    [pageStart, safeWeeksPerPage, weekTotals],
  );

  const maxY = Math.max(
    1,
    Number(maxYProp) || 0,
    Math.ceil(highestWeeklyValue),
  );

  const CHART_WIDTH = 640;
  const CHART_HEIGHT = 240;

  const yAxisWidth = 48;

  const plotTop = 18;
  const plotBottom = 188;

  const plotHeight = plotBottom - plotTop;

  const plotWidth = CHART_WIDTH - yAxisWidth - 12;

  const segmentWidth = plotWidth / safeWeeksPerPage;

  const points = currentData.map((item, index) => {
    const x = yAxisWidth + segmentWidth * index + segmentWidth / 2;

    const y = plotTop + plotHeight - (item.value / maxY) * plotHeight;

    const label = getWeekLabel(item.weekStart, item.weekEnd);

    return {
      ...item,
      x,
      y,

      labelTop: label.top,

      labelBottom: label.bottom,
    };
  });

  const linePath = points
    .map((point, index) => `${index === 0 ? "M" : "L"}${point.x},${point.y}`)
    .join(" ");

  const totalLength = getLineLength(points);

  const yTickValues = Array.from(
    {
      length: yTickCount + 1,
    },
    (_, index) => (maxY / yTickCount) * index,
  );

  const isOlderDisabled = pageOffset >= maxPageOffset;

  const isNewerDisabled = pageOffset === 0;

  const rangeLabel = `${formatRangeDate(pageStart)} – ${formatRangeDate(
    pageEnd,
    pageStart.getFullYear() !== pageEnd.getFullYear() || pageOffset > 0,
  )}`;

  const metricLabel = unit ? `Weekly total · ${unit}` : "Weekly total";

  const goalLabel =
    goalPerDay !== null && goalPerDay !== undefined && Number(goalPerDay) > 0
      ? `Daily goal: ${defaultFormatValue(Number(goalPerDay))}${
          unit ? ` ${unit}` : ""
        }`
      : null;

  const showTooltip = (point) => {
    clearTimeout(tooltipTimer.current);

    setTooltip(point);

    onPointPress?.({
      value: point.value,

      weekStart: formatDateKey(point.weekStart),

      weekEnd: formatDateKey(point.weekEnd),

      items: point.items,
    });

    tooltipTimer.current = setTimeout(() => setTooltip(null), 2200);
  };

  useEffect(() => () => clearTimeout(tooltipTimer.current), []);

  if (!parsedData.length) {
    return <div className="line-chart line-chart--empty">{emptyLabel}</div>;
  }

  return (
    <div className="line-chart">
      <div className="line-chart__header">
        <button
          type="button"
          className="line-chart__nav"
          onClick={() =>
            setPageOffset((value) => Math.min(value + 1, maxPageOffset))
          }
          disabled={isOlderDisabled}
          aria-label="Older weeks"
        >
          <IoChevronBack />
        </button>

        <div className="line-chart__header-center">
          <strong>{rangeLabel}</strong>

          <span className="line-chart__metric">{metricLabel}</span>

          {goalLabel && <span className="line-chart__goal">{goalLabel}</span>}
        </div>

        <button
          type="button"
          className="line-chart__nav"
          onClick={() => setPageOffset((value) => Math.max(value - 1, 0))}
          disabled={isNewerDisabled}
          aria-label="Newer weeks"
        >
          <IoChevronForward />
        </button>
      </div>

      <svg
        className="line-chart__svg"
        viewBox={`0 0 ${CHART_WIDTH} ${CHART_HEIGHT}`}
        role="img"
        aria-label={metricLabel}
      >
        {yTickValues.map((value, index) => {
          const y = plotTop + plotHeight - (value / maxY) * plotHeight;

          return (
            <g key={`y-${index}`}>
              <line
                className="line-chart__grid-line"
                x1={yAxisWidth}
                y1={y}
                x2={CHART_WIDTH - 4}
                y2={y}
              />

              <text
                className="line-chart__axis-text"
                x={yAxisWidth - 8}
                y={y + 4}
                textAnchor="end"
              >
                {formatValue(value)}
              </text>
            </g>
          );
        })}

        <line
          className="line-chart__axis"
          x1={yAxisWidth}
          y1={plotTop}
          x2={yAxisWidth}
          y2={plotBottom}
        />

        {!!linePath && (
          <path
            key={linePath}
            className="line-chart__path"
            d={linePath}
            style={{
              strokeDasharray: Math.max(totalLength, 1),

              "--line-length": Math.max(totalLength, 1),
            }}
          />
        )}

        {points.map((point) => (
          <g key={formatDateKey(point.weekStart)}>
            <circle
              className="line-chart__point-hit"
              cx={point.x}
              cy={point.y}
              r="12"
              onClick={() => showTooltip(point)}
            />

            <circle
              className="line-chart__point"
              cx={point.x}
              cy={point.y}
              r="4.5"
              pointerEvents="none"
            />

            <text
              className="line-chart__x-text"
              x={point.x}
              y={211}
              textAnchor="middle"
            >
              {point.labelTop}
            </text>

            <text
              className="line-chart__x-text"
              x={point.x}
              y={228}
              textAnchor="middle"
            >
              {point.labelBottom}
            </text>
          </g>
        ))}

        {tooltip &&
          (() => {
            const tooltipText = `${formatValue(tooltip.value)}${
              unit ? ` ${unit}` : ""
            }`;

            const boxWidth = Math.max(
              72,
              Math.min(150, 24 + tooltipText.length * 7),
            );

            const boxHeight = 32;

            const x = Math.max(
              yAxisWidth + 2,
              Math.min(tooltip.x - boxWidth / 2, CHART_WIDTH - boxWidth - 4),
            );

            const y =
              tooltip.y < plotTop + 42 ? tooltip.y + 14 : tooltip.y - 42;

            return (
              <g className="line-chart__tooltip">
                <rect x={x} y={y} width={boxWidth} height={boxHeight} rx="6" />

                <text x={x + boxWidth / 2} y={y + 21} textAnchor="middle">
                  {tooltipText}
                </text>
              </g>
            );
          })()}
      </svg>
    </div>
  );
}
